import { formatMoney, type PublicPaymentDto } from '@bqcrm/shared';
import { Alert, Box, Button, Card, Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft, IconCircleCheck, IconClock, IconCreditCard } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, errorText } from '../api';
import { dayLong } from '../format';
import { portalReturnPath } from './portal/portalReturn';

/**
 * Strona „Zapłać online” z linku w e-mailu (docs/24): bez logowania i bez danych osobowych — dokument, kwota
 * do zapłaty (z serwera) i przejście do bramki ING Pay. Po powrocie z bramki (`?powrot=1`) serwer sprawdza stan
 * płatności w ING Pay; do potwierdzenia z banku strona odświeża się sama.
 */
export function PublicPaymentPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const back = params.get('powrot') === '1';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // płatność rozpoczęta w strefie klienta (w tej karcie) — przycisk powrotu do strefy
  const [portalPath] = useState(portalReturnPath);
  const q = useQuery({
    queryKey: ['public-payment', token, back],
    queryFn: () => api<PublicPaymentDto>(`/public/platnosc/${token}${back ? '?powrot=1' : ''}`),
    retry: false,
    refetchInterval: (query) => (query.state.data?.state === 'w_toku' ? 5000 : false),
  });

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ url: string }>(`/public/platnosc/${token}`, { method: 'POST' });
      window.location.assign(r.url);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
      void q.refetch();
    }
  };

  if (q.isLoading)
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  if (q.error || !q.data)
    return (
      <Container size="xs" py={80}>
        <Alert color="red" title="Płatność niedostępna" icon={<IconAlertTriangle />}>
          {q.error ? errorText(q.error) : 'Nie znaleziono płatności'}
        </Alert>
      </Container>
    );
  const v = q.data;
  return (
    <Box bg="var(--app-subtle)" mih="100vh" py={48}>
      <Container size="xs">
        <Card withBorder radius="md" padding="lg">
          <Stack gap="md">
            <div>
              <Text size="sm" c="dimmed">
                {v.sellerName}
              </Text>
              <Title order={3}>
                {v.documentLabel} {v.number}
              </Title>
              {v.paymentTo && v.state === 'do_zaplaty' && (
                <Text size="sm" c="dimmed">
                  termin płatności {dayLong(v.paymentTo, { weekday: false })}
                </Text>
              )}
            </div>
            {v.state === 'do_zaplaty' && (
              <>
                <div>
                  <Text size="sm" c="dimmed">
                    Do zapłaty
                  </Text>
                  <Text fz={32} fw={700}>
                    {formatMoney(v.toPayGr)}
                  </Text>
                </div>
                {back && (
                  <Alert color="orange" icon={<IconAlertTriangle />}>
                    Płatność nie została zakończona. Możesz spróbować ponownie.
                  </Alert>
                )}
                {error && <Alert color="red">{error}</Alert>}
                <Button size="md" leftSection={<IconCreditCard size={20} />} onClick={() => void pay()} loading={busy}>
                  Przejdź do płatności
                </Button>
                <Text size="xs" c="dimmed">
                  BLIK, karta płatnicza, szybki przelew — bezpieczna płatność w serwisie ING Pay (ING Bank Śląski). Wpłata zostanie zaksięgowana automatycznie.
                </Text>
              </>
            )}
            {v.state === 'w_toku' && (
              <Alert color="blue" icon={<IconClock />} title="Czekamy na potwierdzenie z banku">
                Płatność jest w trakcie realizacji. Ta strona odświeży się sama — możesz ją też zamknąć, potwierdzenie zapisze się bez Twojego udziału.
              </Alert>
            )}
            {v.state === 'oplacona' && (
              <Alert color="green" icon={<IconCircleCheck />} title="Opłacone">
                Dziękujemy! Dokument jest opłacony — nie trzeba nic więcej robić.
              </Alert>
            )}
            {v.state === 'niedostepna' && (
              <Alert color="gray" icon={<IconAlertTriangle />} title="Płatność online niedostępna">
                Tego dokumentu nie można teraz zapłacić online. Zapłać przelewem na rachunek podany w wiadomości albo na dokumencie.
              </Alert>
            )}
            {portalPath && (
              <Button component={Link} to={portalPath} variant={v.state === 'oplacona' ? 'filled' : 'default'} leftSection={<IconArrowLeft size={18} />}>
                Wróć do strefy klienta
              </Button>
            )}
          </Stack>
        </Card>
      </Container>
    </Box>
  );
}
